import { FC, useState } from 'react';
import cx from 'classnames';
import { isMobile } from 'react-device-detect';

import { AppTracker, ShareType } from 'types';
import { getEventURL } from 'utils';
import { eventNameSelector } from 'store/selectors';
import { tracker } from 'services';
import { useStore } from 'hooks';
import { useLiveEvent } from 'containers/LiveEvent';
import { Divider, TextArea } from 'components';
import { CheckedIcon, CopyIcon, ThreeDotsIcon } from 'components/Icon';

import styles from './LiveEvent.Share.module.scss';

export type ShareProps = {
  inviteeCode?: string;
};

const Share: FC<ShareProps> = ({ inviteeCode }) => {
  const liveEvent = useLiveEvent();
  const eventName = useStore(eventNameSelector(liveEvent.eventCode));

  const [copied, setCopied] = useState(false);

  const generatePublicLink = () => {
    const url = window.location.toString();
    const indexOfInviteeCode = url.indexOf(inviteeCode || '');
    const hasInviteeCode = indexOfInviteeCode > 0;

    const publicLink = hasInviteeCode ? url.substring(0, indexOfInviteeCode - 1) : url;

    return publicLink;
  };

  const [text, changeText] = useState<string>(
    () => `Hi, please join “${eventName}” event with this link ${generatePublicLink()}`
  );

  const onChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => changeText(e.target.value);

  const handleOnShare = () => {
    tracker.shareEventLink(liveEvent.eventCode, ShareType.NATIVE, AppTracker.ShareSource.SHARE_WITH_FRIENDS_PANEL);

    try {
      navigator.share({
        url: getEventURL(liveEvent.channelName, liveEvent.eventCode, true)
      });
    } catch (e) {
      alert('Not supported');
    }
  };

  const handleOnCopied = () => {
    tracker.shareEventLink(liveEvent.eventCode, ShareType.COPY, AppTracker.ShareSource.SHARE_WITH_FRIENDS_PANEL);

    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2_000);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <TextArea value={text} maxLength={200} onChange={onChangeText} />
      </div>

      <div className={styles.buttons}>
        <button type="button" className={cx(styles.copy, { [styles.copied]: copied })} onClick={handleOnCopied}>
          {copied ? <CheckedIcon /> : <CopyIcon />}
          <span>{copied ? 'Copied' : 'Copy link'}</span>
        </button>
      </div>

      {isMobile && (
        <>
          <Divider text="Or" />
          <button type="button" className={styles.more} onClick={handleOnShare}>
            <ThreeDotsIcon width={18} height={18} className="mr-6" />
            <span>More options</span>
          </button>
        </>
      )}
    </div>
  );
};

export default Share;
